import { DossierData, FiliationData, Property, PropertyData, ValidatedProperty } from "@/taskpane/types/filiation";
import { apiClient } from "@/taskpane/utils/client";

interface ErrorWithStatus extends Error {
  status?: number;
}

/** Réponse possible de l’API diagramme : données complètes ou tronquées avec URL pré-signée. */
interface PublicFiliationApiResponse {
  is_truncated?: boolean;
  full_data_url?: string;
  nodes?: unknown[];
  origines?: unknown[];
  origines_by_date?: Record<string, unknown>;
  cadastre_filiation_diagramme?: unknown;
  [key: string]: unknown;
}

/**
 * Retrieves the filiation data for a given folder and subfolder.
 *
 * @param {Object} options - The options for retrieving the filiation data.
 * @param {string} options.folderId - The ID of the folder.
 * @param {string} options.flowId - The ID of the flow.
 * @returns {Promise<Property[]>} - A promise that resolves to an array of filiation properties.
 */
export async function getFiliation({
  folderId,
  flowId,
}: {
  folderId: string;
  flowId: string;
}): Promise<PropertyData | undefined> {
  const response = await apiClient.get(`/dossier/${folderId}/filiation/${flowId}`);
  return response?.data;
}

/**
 * Sets the filiation for a given folder and subfolder.
 * Error handling is done locally in the component calling this function.
 *
 * @param {Object} options - The options for setting the filiation.
 * @param {string} options.folderId - The ID of the folder.
 * @param {string} options.subFolderId - The ID of the subfolder.
 * @param {ValidatedProperty[]} options.properties - The properties to set for the filiation.
 * @returns {Promise<void>} - A promise that resolves when the filiation is set.
 */
export async function setFiliation({
  folderId,
  flowId,
  properties,
}: {
  folderId: string;
  flowId: string;
  properties: ValidatedProperty[];
}) {
  return apiClient.post(`/dossier/${folderId}/filiation/${flowId}`, properties);
}


/**
 * Locks a filiation to prevent further modifications.
 * Error handling is done locally in the component calling this function.
 *
 * @param {Object} options - The options for locking the filiation.
 * @param {string} options.folderId - The ID of the folder.
 * @param {string} options.subFolderId - The ID of the subfolder.
 * @returns {Promise<void>} - A promise that resolves when the filiation is locked.
 */
export async function lockFiliation({
  folderId,
  flowId,
  lockValue,
}: {
  folderId: string;
  flowId: string;
  lockValue: boolean;
}) {
  const body = {
    'lock_status': lockValue
  }
  return apiClient.put(`/dossier/${folderId}/filiation/${flowId}/lock_filiation`, body);
}


/**
 * Retrieves the filiation data for the diagramme (public endpoint).
 * When the payload exceeds the backend limit, the API returns is_truncated and full_data_url;
 * this function then fetches the full data from the presigned S3 URL and returns it.
 *
 * @param {Object} options - The options for retrieving the filiation data.
 * @param {string} options.clientId - The ID of the client.
 * @param {string} options.dossierId - The ID of the folder.
 * @param {string} options.sousDossierId - The ID of the subfolder.
 * @param {string} options.flowId - The ID of the flow link to filiation.
 * @returns {Promise<Property[] | null>} - The filiation data (full payload) or null on error.
 */
export async function getPublicFiliation({
  clientId,
  dossierId,
  sousDossierId,
  flowId
}: {
  clientId: string;
  dossierId: string;
  sousDossierId: string;
  flowId: string;
}): Promise<Property[] | null> {
  try {
    const response = await apiClient.get(`/public/filiation/${clientId}/${dossierId}/${sousDossierId}/${flowId}`);
    const data = response?.data as PublicFiliationApiResponse | undefined;

    const presignedUrl =
      typeof data?.full_data_url === "string" && data.full_data_url.length > 0 ? data.full_data_url : null;
    if (data?.is_truncated === true && presignedUrl) {
      const fullResponse = await fetch(presignedUrl);
      if (!fullResponse.ok) {
        console.error(
          `Échec de la récupération des données complètes depuis l'URL pré-signée : ${fullResponse.status} ${fullResponse.statusText}`
        );
        return null;
      }
      const fullData = await fullResponse.json();
      return fullData;
    }

    return data ?? null;
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : (error as ErrorWithStatus).status != null
            ? `code erreur : ${(error as ErrorWithStatus).status}`
            : "";

    // Log l'erreur dans la console sans afficher de snackbar - le modal gère l'affichage
    console.error(
      `GET request to api endpoint /public/filiation/diagramme/${clientId}/${dossierId}/${sousDossierId}/${flowId} failed ${errorMessage ? `with status ${errorMessage}` : ""}`,
      error
    );
    return null;
  }
}